/**
 * useAccessOverrides — fetches the CURRENT USER's per-user access overrides.
 *
 * Endpoint: GET /api/access-control/me/overrides
 *
 * Returns: { overrides, loaded }
 *   overrides: { dropdowns: { <key>: bool }, items: { '<dKey>.<iKey>': bool } }
 *   loaded:    boolean — true once a fresh fetch (or cached read) has resolved.
 *
 * Semantics:
 *   true  → granted (force-show even if role wouldn't get it by default)
 *   false → blocked (force-hide even if role would get it by default)
 *   absent→ default (use static config in /config/accessControl.js)
 *
 * Cached for 5 minutes; invalidated when an override is saved (window
 * `pie:access-overrides-updated` event) or when the user logs out.
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/utils';

const CACHE_KEY = 'pie:access-overrides:v2';
const TTL_MS = 5 * 60 * 1000;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > TTL_MS) return null;
    return data || {};
  } catch (_) { return null; }
}

export default function useAccessOverrides() {
  const { user } = useAuth();
  const cached = readCache();
  const [overrides, setOverrides] = useState(cached || {});
  // `loaded` is true if the cache had a value (so the guard can trust it
  // immediately) OR once a network fetch resolves.
  const [loaded, setLoaded] = useState(cached !== null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiRequest('/access-control/me/overrides');
      setOverrides(data || {});
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
    } catch (_) { /* keep stale data; we still mark loaded so the guard
                    doesn't block the user forever on a transient error. */ }
    finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener('pie:access-overrides-updated', onUpdate);
    return () => window.removeEventListener('pie:access-overrides-updated', onUpdate);
  }, [refresh]);

  // Always return a plain object with the same shape Sidebar/MobileNav
  // already expect, plus a `_loaded` flag the route-guard reads. Existing
  // callers that just spread overrides.dropdowns / overrides.items keep
  // working as before.
  return {
    dropdowns: overrides.dropdowns || {},
    items: overrides.items || {},
    _loaded: loaded,
  };
}
